import type { TFunction } from 'i18next';
import { ApiError, estApiError } from './errors.js';

/**
 * Erreurs de validation par champ. L'API renvoie
 * `400 validation.invalide` avec `details.champs: [{ chemin, message }]`
 * (`apps/api/src/errors.ts`, `erreurValidation`) : le `chemin` pointe le champ
 * fautif (`titre`, `dimensions.hauteur`, `lignes.0.designation`), le `message`
 * est celui du schéma — français quand le schéma en porte un, sinon le texte
 * par défaut de Zod, en anglais, que l'on traduit ici.
 */
export interface ErreurChamp {
  readonly chemin: string;
  readonly message: string;
}

export function champsEnErreur(erreur: unknown): readonly ErreurChamp[] {
  if (!estApiError(erreur) || erreur.code !== 'validation.invalide') return [];
  const details = erreur.details;
  if (typeof details !== 'object' || details === null) return [];
  const champs = (details as { champs?: unknown }).champs;
  if (!Array.isArray(champs)) return [];
  return champs.flatMap((champ: unknown) => {
    if (typeof champ !== 'object' || champ === null) return [];
    const { chemin, message } = champ as { chemin?: unknown; message?: unknown };
    return typeof chemin === 'string' && typeof message === 'string' ? [{ chemin, message }] : [];
  });
}

/**
 * Messages par défaut de Zod 4 (anglais) → texte pour la personne. Un message
 * qui ne leur ressemble pas est déjà écrit pour elle : il passe tel quel.
 */
export function traduireMessageChamp(message: string, t: TFunction): string {
  const trop = /^Too small: expected string to have >=(\d+) characters?$/.exec(message);
  if (trop) {
    const minimum = Number(trop[1]);
    return minimum <= 1
      ? t('regie:validation.obligatoire')
      : t('regie:validation.auMoinsCaracteres', { count: minimum });
  }
  const long = /^Too big: expected string to have <=(\d+) characters?$/.exec(message);
  if (long) return t('regie:validation.auPlusCaracteres', { count: Number(long[1]) });
  const petit = /^Too small: expected (?:number|int) to be >=?\s*(-?[\d.]+)$/.exec(message);
  if (petit) return t('regie:validation.valeurMin', { valeur: petit[1] });
  const grand = /^Too big: expected (?:number|int) to be <=?\s*(-?[\d.]+)$/.exec(message);
  if (grand) return t('regie:validation.valeurMax', { valeur: grand[1] });
  if (/^Too small: expected array/.test(message)) return t('regie:validation.listeVide');
  if (/received undefined$/.test(message)) return t('regie:validation.obligatoire');
  if (/^Invalid input: expected int\b/.test(message)) return t('regie:validation.entier');
  if (/^Invalid input: expected number\b/.test(message)) return t('regie:validation.nombre');
  if (/^Invalid input: expected string\b/.test(message)) return t('regie:validation.texte');
  if (/^Invalid email/i.test(message)) return t('regie:validation.email');
  if (/^Invalid option/.test(message)) return t('regie:validation.option');
  if (/^Invalid string: must match pattern/.test(message)) return t('regie:validation.format');
  if (/^Unrecognized key/.test(message)) return t('regie:validation.champInconnu');
  if (/^Invalid input$/.test(message)) return t('regie:validation.invalide');
  return message;
}

/** Messages indexés par chemin ; le premier message d'un champ gagne. */
export function erreursParChamp(erreur: unknown, t: TFunction): Record<string, string> {
  const resultat: Record<string, string> = {};
  for (const { chemin, message } of champsEnErreur(erreur)) {
    resultat[chemin] ??= traduireMessageChamp(message, t);
  }
  return resultat;
}

/** Dernier segment non numérique d'un chemin (`emails.0` → `emails`). */
function segmentDuChamp(chemin: string): string {
  const segments = chemin.split('.').filter((segment) => !/^\d+$/.test(segment));
  return segments[segments.length - 1] ?? chemin;
}

/**
 * Le message d'une erreur de validation qui nomme les champs fautifs, pour les
 * formulaires qui n'affichent qu'une ligne d'erreur : « Les données envoyées
 * sont invalides : Titre — Ce champ est obligatoire. »
 */
export function messageValidationDetaille(message: string, details: unknown, t: TFunction): string {
  const champs = champsEnErreur(new ApiError(400, 'validation.invalide', message, details ?? null));
  if (champs.length === 0) return message;
  const lignes = champs.slice(0, 5).map(({ chemin, message: texte }) => {
    const segment = segmentDuChamp(chemin);
    const libelle = t(`regie:validation.champs.${segment}`, { defaultValue: segment });
    return `${libelle} — ${traduireMessageChamp(texte, t)}`;
  });
  return `${message.replace(/\.$/, '')} : ${lignes.join(' ')}`;
}
